// The jobs a post can do, what each is made from (fields and photos), and
// the first words for every design. A post is job + design + colourway: the
// words stay when the design or colour changes, and words the salon hasn't
// touched take the new design's own first words.
//
// A design is one of the three styles (post.family: editorial, poster,
// quiet), or one of the job's own layouts (post.family 'layouts' and
// post.layout: a review receipt, a studio ID, a three-step guide). A field
// or photo with `only` belongs to the designs it names. An Announcement can
// also wear a season's own designs (../kits), as layouts it finds by id.
import { IMG } from '../../email-composer/data/templates'
import { seasonalLayout } from '../kits'
import { FAMILY_ORDER } from '../render/families'
import { COLOURWAYS, colourwayInFamily } from '../render/colourways'
import { isoIn } from './dates'

const STOCK = {
  hair: { work: IMG.hairBrunetteGloss, team: IMG.portraitBun },
  barber: { work: IMG.barberFade, team: IMG.barberHat },
  beauty: { work: IMG.nailsMinimal, team: IMG.facialClinic },
}
export const stockFor = (trade) => STOCK[trade] || STOCK.hair
export const isStock = (src) => /images\.unsplash\.com/.test(String(src || ''))

// Stock photos a salon can pick from, by trade
export const STOCK_LIBRARY = {
  hair: [IMG.hairBrunetteGloss, IMG.hairBlondeWaves, IMG.hairCurlsJoy, IMG.hairCopper, IMG.hairAsh, IMG.hairSilver, IMG.hairBlowdry, IMG.hairCape, IMG.salonBright, IMG.salonMirrors, IMG.portraitBun, IMG.hairProductsShelf],
  barber: [IMG.barberFade, IMG.barberBeardProfile, IMG.barberClient, IMG.barberBeardTrim, IMG.barberTowel, IMG.barberCurls, IMG.barberLong, IMG.barberBrick, IMG.barberRedChair, IMG.barberTools, IMG.barberScissors, IMG.barberSign],
  beauty: [IMG.nailsMinimal, IMG.nailsNude, IMG.nailsFlowers, IMG.nailsTech, IMG.lashLift, IMG.browShaping, IMG.facialClinic, IMG.facialMask, IMG.spaRobe, IMG.spaBath, IMG.giftRoses, IMG.giftPink],
}

const photo = (src) => (src ? { src, fx: 0.5, fy: 0.4, zoom: 1 } : null)
// Wording by trade: hair, barber, beauty
// The words for a trade. Beauty can be one line or, where nails, lashes and
// skin need different words, an object by specialism ({ nails, lashes, skin,
// beauty }); the trade then comes as 'beauty:lashes' (makePost), and a plain
// 'beauty' gets the words that suit any beauty business.
const byTrade = (trade, hair, barber, beauty) => {
  const [t, spec] = String(trade || '').split(':')
  if (t === 'barber') return barber
  if (t !== 'beauty') return hair
  if (beauty && typeof beauty === 'object' && !Array.isArray(beauty)) return beauty[spec] || beauty.beauty
  return beauty
}
const CTA = 'Book through the link in bio'
const ITALIC_HINT = 'Wrap words in *asterisks* to set them in italics (Editorial and Quiet).'
const STYLES = ['editorial', 'poster', 'quiet']
const REVIEW_LAYOUTS = ['quote', 'receipt', 'conversation', 'rating', 'note']
// The booking kit's layouts, for free slots and a single free slot alike
// (each takes one to four times). The slot drop has no small heading: it
// says the day and service there instead.
const BOOKING = ['ticket', 'orbit', 'agenda', 'drop', 'notice']
const BOOKING_SAID = ['ticket', 'orbit', 'agenda', 'notice']
const bookingLayouts = (one) => ({
  ticket: { name: 'The appointment ticket', blurb: 'A perforated ticket, with the times as the main event', theme: 'electric', content: () => ({ [one ? 'line' : 'eyebrow']: 'Admit one / A little time for you', headline: 'YOUR NAME\nON IT.', cta: 'Book your time, link in bio' }) },
  orbit: { name: 'Time in your orbit', blurb: 'The times in circles round a big message', theme: 'lilac', content: () => ({ [one ? 'line' : 'eyebrow']: one ? 'Just opened' : 'Openings', headline: 'A little\nyou time.', cta: 'Book your time, link in bio' }) },
  agenda: { name: 'The studio agenda', blurb: 'A daybook: the day down the side, the times in rows', theme: 'olive', content: () => ({ [one ? 'line' : 'eyebrow']: 'The appointment edit', headline: 'A good day\nstarts here.', cta: 'Book your time, link in bio' }) },
  drop: { name: 'The slot drop', blurb: 'Loud, with the times on tilted cards', theme: 'cherry', ground: 'accent', content: () => ({ headline: one ? 'A GOOD TIME.\nJUST DROPPED.' : 'GOOD TIMES.\nJUST DROPPED.', cta: 'Book your time, link in bio' }) },
  notice: { name: 'The take-a-time poster', blurb: 'A noticeboard with tear-off tabs', theme: 'electric', content: () => ({ [one ? 'line' : 'eyebrow']: 'Take a time. Make it yours.', headline: 'THIS SPACE\nIS FOR YOU.', cta: 'Book your time, link in bio' }) },
})

export const POST_TYPES = {
  freeSlots: {
    label: 'Free slots', short: 'Free slots', blurb: 'Fill the gaps in your diary',
    photos: [],
    fields: [
      { key: 'eyebrow', label: 'Small heading', type: 'text', only: [...STYLES, ...BOOKING_SAID] },
      { key: 'headline', label: 'Headline', type: 'text', hint: ITALIC_HINT },
      { key: 'date', label: 'Day', type: 'date', hint: 'Shown as Today, Tomorrow or the day’s name, on the day you post.' },
      { key: 'times', label: 'Times', type: 'list', itemLabel: 'Time', placeholder: '2pm', max: 3, layoutMax: 4, layoutHint: 'Four fit this design. Switch on “Say there are more times” for the rest.' },
      { key: 'service', label: 'What they’re for', type: 'text', placeholder: 'Cut and finish', only: BOOKING, hint: 'Leave it empty when any service fits.' },
      { key: 'more', label: 'Say there are more times', type: 'toggle' },
      { key: 'cta', label: 'Booking line', type: 'text' },
    ],
    content: () => ({ eyebrow: 'Last-minute appointments', headline: 'Time for you.', date: isoIn(1), times: ['10am', '1:30pm', '4pm'], service: '', more: false, cta: CTA }),
    designs: {
      editorial: { name: 'The appointment edit', content: () => ({ headline: 'Time for *you*.' }) },
      poster: { name: 'Open chairs', content: (t) => ({ headline: byTrade(t, 'Chairs free.', 'Chairs free.', 'Slots free.'), eyebrow: 'Last-minute' }) },
      quiet: { name: 'Your hour off', content: () => ({ headline: 'Your hour *off*.' }) },
    },
    layouts: bookingLayouts(false),
  },
  oneSlot: {
    label: 'One free slot', short: 'One slot', blurb: 'A cancellation, filled fast',
    photos: [],
    fields: [
      { key: 'headline', label: 'Headline', type: 'text', hint: ITALIC_HINT },
      { key: 'date', label: 'Day', type: 'date', hint: 'Shown as Today, Tomorrow or the day’s name, on the day you post.' },
      { key: 'time', label: 'Time', type: 'text', placeholder: '2:30pm' },
      { key: 'service', label: 'Service', type: 'text' },
      { key: 'line', label: 'Small line', type: 'text', only: [...STYLES, ...BOOKING_SAID] },
      { key: 'cta', label: 'Booking line', type: 'text' },
    ],
    content: (t) => ({ headline: 'A space just opened.', date: isoIn(1), time: '2:30pm', service: byTrade(t, 'Cut and finish', 'Skin fade', 'Gel manicure'), line: byTrade(t, 'One chair. Your name on it.', 'One chair. Your name on it.', 'One space. Your name on it.'), cta: CTA }),
    designs: {
      editorial: { name: 'The one slot', content: () => ({ headline: 'A space just *opened*.' }) },
      poster: { name: 'The empty chair', content: (t) => ({ headline: byTrade(t, 'Chair. Waiting.', 'Chair. Waiting.', 'Space. Waiting.'), cta: 'Book now, link in bio' }) },
      quiet: { name: 'Space for you', content: () => ({ headline: 'Just for *you*.' }) },
    },
    layouts: bookingLayouts(true),
  },
  freshWork: {
    label: 'Fresh work', short: 'Fresh work', blurb: 'One great photo of your work',
    photos: [{ key: 'main', label: 'Photo' }],
    fields: [
      { key: 'detail', label: 'Small line', type: 'text', placeholder: 'Precision cut / soft movement' },
      { key: 'headline', label: 'Headline', type: 'text', hint: ITALIC_HINT },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: (t) => ({ detail: byTrade(t, 'Precision cut / soft movement', 'Low taper / textured top', 'Soft almond / BIAB'), headline: byTrade(t, 'The bob. Reimagined.', 'Clean cut. Clear head.', { nails: 'Nails that last.', lashes: 'Lashes, lifted.', skin: 'Skin, refreshed.', beauty: 'Fresh from the chair.' }), cta: 'Save this for your next visit' }),
    stock: (t) => ({ main: photo(stockFor(t).work) }),
    designs: {
      editorial: { name: 'The look book', content: (t) => ({ headline: byTrade(t, 'The bob. *Reimagined.*', 'Clean cut. *Clear head.*', { nails: 'Nails that *last*.', lashes: 'Lashes, *lifted*.', skin: 'Skin, *refreshed*.', beauty: 'Fresh from *the chair*.' }) }) },
      poster: { name: 'The fresh finish', content: () => ({ cta: 'Your next look? Book in bio' }) },
      quiet: { name: 'The moment of calm', content: (t) => ({ headline: 'A little *me time*.', detail: byTrade(t, 'Your cut. Your moment.', 'Your cut. Your moment.', 'Your treatment. Your moment.') }) },
    },
  },
  beforeAfter: {
    label: 'Before and after', short: 'Before & after', blurb: 'A real result, side by side',
    photos: [{ key: 'before', label: 'Before' }, { key: 'after', label: 'After' }],
    fields: [
      { key: 'headline', label: 'Headline', type: 'text', hint: ITALIC_HINT },
      { key: 'detail', label: 'Who and what', type: 'text', placeholder: 'Balayage by Jess' },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: (t) => ({ headline: 'A fresh perspective.', detail: byTrade(t, 'Colour by Jess', 'Cut by Marcus', 'Brows by Priya'), cta: 'Save the look, book in bio' }),
    designs: {
      editorial: { name: 'The real result', content: () => ({ headline: 'A fresh *perspective*.' }) },
      poster: { name: 'Side by side', content: () => ({ headline: 'Before. After.' }) },
      quiet: { name: 'The reveal', content: () => ({ headline: 'The *reveal*.' }) },
    },
    // From the before and after kit
    layouts: {
      split: { name: 'The editorial split', blurb: 'Two equal portraits, side by side', theme: 'paper', content: () => ({ headline: 'A fresh perspective.' }) },
      stacked: { name: 'The stacked reveal', blurb: 'Before, then after, on wide cards', theme: 'rose', content: () => ({ headline: 'Same you. New energy.' }) },
      afterSpotlight: { name: 'The after spotlight', blurb: 'The finished look, big, with the before inset', theme: 'electric', content: () => ({ headline: 'THE NEXT\nCHAPTER.' }) },
      contactSheet: { name: 'The contact sheet', blurb: 'Two frames of film on a dark ground', theme: 'paper', ground: 'ink', content: () => ({ headline: 'From this. To this.' }) },
      prints: { name: 'The studio prints', blurb: 'Two prints, pinned at an angle', theme: 'sage', content: () => ({ headline: 'A little change.\nA whole new feeling.' }) },
    },
  },
  review: {
    label: 'Review', short: 'Review', blurb: 'Kind words, word for word',
    photos: [],
    fields: [
      { key: 'headline', label: 'Headline', type: 'text', only: REVIEW_LAYOUTS },
      { key: 'quote', label: 'The review', type: 'textarea', hint: 'Quoted word for word. You can shorten it with … but not change it.', locked: true },
      { key: 'name', label: 'Their name', type: 'text', hint: 'First name or initial only.' },
      { key: 'service', label: 'Service', type: 'text' },
      { key: 'rating', label: 'Stars', type: 'stars' },
      { key: 'reply', label: 'Your reply', type: 'text', only: ['conversation'] },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: (t) => ({
      headline: 'Kind words.',
      quote: byTrade(t, 'Best haircut I’ve had in years. Jess listened, and it still looks great a month later.', 'Proper barber. Great chat, sharp fade, in and out in 30 minutes.', 'My nails lasted three weeks without a single chip. Already booked my next set.'),
      name: 'Hannah', service: byTrade(t, 'Cut and colour', 'Skin fade', 'BIAB manicure'), rating: 5,
      reply: 'This is why we do it. Thank you.', cta: CTA,
    }),
    designs: {
      editorial: { name: 'In their words' },
      poster: { name: 'Five stars' },
      quiet: { name: 'Kind words' },
    },
    layouts: {
      quote: { name: 'The editorial quote', blurb: 'A big quotation mark and room to breathe', theme: 'paper', content: () => ({ headline: 'Kind words. Good energy.' }) },
      receipt: { name: 'The review receipt', blurb: 'Printed like a receipt, with their stars', theme: 'electric', ground: 'accent', content: () => ({ headline: 'THE GOOD WORD.' }) },
      conversation: { name: 'The conversation', blurb: 'Their words in a speech bubble, and your reply', theme: 'rose', content: () => ({ headline: 'You said it.' }) },
      rating: { name: 'The rating poster', blurb: 'The stars they gave, as big as they go', theme: 'electric', ground: 'accent', content: () => ({ headline: 'A little client love.' }) },
      note: { name: 'The thank-you note', blurb: 'Pinned up like a note on the wall', theme: 'sage', content: () => ({ headline: 'Made our day.' }) },
    },
  },
  team: {
    label: 'Meet the team', short: 'Team', blurb: 'Introduce someone',
    photos: [{ key: 'main', label: 'Their photo', only: [...STYLES, 'cover', 'studioId', 'threeThings', 'feature'] }],
    fields: [
      { key: 'eyebrow', label: 'Small heading', type: 'text', only: [...STYLES, 'monogram', 'threeThings', 'feature'] },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'role', label: 'Role', type: 'text' },
      { key: 'line', label: 'A line about them', type: 'textarea', only: [...STYLES, 'cover', 'monogram', 'feature'] },
      { key: 'facts', label: 'Three things about them', type: 'pairs', max: 3, only: ['studioId', 'threeThings'], hint: 'Check these with them before you post.' },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: (t) => ({
      eyebrow: 'Meet the team',
      name: byTrade(t, 'Jess', 'Marcus', 'Priya'),
      role: byTrade(t, 'Colour specialist', 'Senior barber', 'Nail and lash artist'),
      line: byTrade(t, 'Ten years of lived-in colour and cuts that grow out beautifully.', 'Skin fades, beard work and hot towel shaves.', 'BIAB, nail art and the neatest lash lifts in town.'),
      facts: [{ q: 'My approach', a: 'Time to listen' }, { q: 'My favourite detail', a: 'A warm welcome' }, { q: byTrade(t, 'Outside the salon', 'Outside the shop', 'Outside the studio'), a: 'Long weekend walks' }],
      cta: CTA,
    }),
    stock: (t) => ({ main: photo(stockFor(t).team) }),
    designs: {
      editorial: { name: 'The profile' },
      poster: { name: 'In the chair' },
      quiet: { name: 'Say hello' },
    },
    layouts: {
      cover: { name: 'The cover portrait', blurb: 'Their photo, big, like a magazine cover', theme: 'paper', content: () => ({ cta: 'Meet the team, book in bio' }) },
      studioId: {
        name: 'The studio ID', blurb: 'An ID card with three things about them', theme: 'electric', ground: 'accent',
        content: (t) => ({
          facts: [{ q: 'Specialty', a: byTrade(t, 'lived-in colour', 'textured cuts', 'brows and lashes') }, { q: 'Approach', a: 'listen first' }, { q: 'Favourite bit', a: byTrade(t, 'the final reveal', 'the final line-up', 'the final reveal') }],
          cta: 'Get to know us, link in bio',
        }),
      },
      monogram: { name: 'The name poster', blurb: 'No photo needed: their initial, big', theme: 'rose', content: () => ({ eyebrow: 'Say hello to our new team member', cta: 'Say hello, link in bio' }) },
      threeThings: { name: 'Three things about me', blurb: 'A small photo and three questions answered', theme: 'sage', content: () => ({ eyebrow: 'Three things to know', cta: 'Say hello at your next visit' }) },
      feature: { name: 'The team feature', blurb: 'Their photo beside their name', theme: 'rose', content: (t) => ({ eyebrow: 'Good people. Great company.', cta: byTrade(t, 'Find your stylist, link in bio', 'Find your barber, link in bio', 'Find your therapist, link in bio') }) },
    },
  },
  newService: {
    label: 'New service', short: 'New service', blurb: 'Something new on your menu',
    photos: [{ key: 'main', label: 'Photo', only: ['treatment', 'spotlight'] }],
    fields: [
      { key: 'eyebrow', label: 'Small heading', type: 'text' },
      { key: 'headline', label: 'The service', type: 'text' },
      { key: 'body', label: 'A line about it', type: 'textarea', only: ['launch', 'treatment', 'invite', 'spotlight'] },
      { key: 'items', label: 'What’s included', type: 'list', itemLabel: 'Thing', max: 3, only: ['included'], fullHint: 'Three fit this design.' },
      { key: 'detail', label: 'Small line', type: 'text', placeholder: 'From £45 / 45 minutes', only: ['launch', 'included', 'invite', 'spotlight'], hint: 'Check any price or time before you post.' },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: (t) => ({
      eyebrow: 'New to the menu',
      headline: byTrade(t, 'The gloss refresh.', 'Beard detailing.', 'The reset facial.'),
      body: byTrade(t, 'A fresh colour finish. A little time for you.', 'A considered shape. Clean edges. Your look, refined.', 'A little space in your day. A treatment chosen around you.'),
      items: byTrade(t, ['A proper consultation', 'A gloss matched to your colour', 'Styling advice to take home'], ['A proper consultation', 'A tailored cut and beard shape', 'Styling advice to take home'], ['A skin consultation', 'A facial chosen for your skin', 'Aftercare advice to take home']),
      detail: byTrade(t, 'From £45 / 45 minutes', 'From £18 / 25 minutes', 'From £55 / 60 minutes'),
      cta: 'Explore the service, link in bio',
    }),
    stock: (t) => ({ main: photo(stockFor(t).work) }),
    designs: {},
    layouts: {
      launch: {
        name: 'The big announcement', blurb: 'NEW, as big as it goes', theme: 'electric', ground: 'accent',
        content: (t) => ({ headline: byTrade(t, 'THE GLOSS\nREFRESH.', 'BEARD\nDETAILING.', 'THE RESET\nFACIAL.'), detail: byTrade(t, 'Ask us about your next colour visit', 'Ask us at your next cut', 'Ask us at your next visit') }),
      },
      treatment: {
        name: 'The treatment editorial', blurb: 'A photo with an editorial caption', theme: 'rose',
        content: (t) => ({ eyebrow: byTrade(t, 'Introducing / The gloss refresh', 'Introducing / Beard detailing', 'Introducing / The reset facial'), headline: byTrade(t, 'Your new\nfavourite finish.', 'Your sharpest\nlook yet.', 'Your new\nhour off.'), cta: 'Discover the details, link in bio' }),
      },
      included: {
        name: 'What’s included', blurb: 'The three things they get', theme: 'sage',
        content: (t) => ({ eyebrow: byTrade(t, 'New service / Gloss and tone', 'New service / Cut and beard', 'New service / Facial'), headline: byTrade(t, 'The complete\ncolour refresh.', 'The complete\nchair reset.', 'The complete\nskin reset.'), detail: 'One appointment. The whole routine.', cta: 'Find your next appointment' }),
      },
      invite: {
        name: 'The invitation', blurb: 'A ticket to try it', theme: 'paper',
        content: (t) => ({ eyebrow: 'You’re invited', headline: byTrade(t, 'The signature\nblow-dry.', 'The signature\nhot towel shave.', 'The signature\nfacial.'), body: byTrade(t, 'Movement. Shape. A finish that feels like you.', 'Hot towels, a close shave and twenty quiet minutes.', 'Calm, glow and a little time just for you.'), detail: 'Now on the menu', cta: 'Book your first visit, link in bio' }),
      },
      spotlight: {
        name: 'The service spotlight', blurb: 'A photo beside the price and time', theme: 'electric',
        content: (t) => ({ eyebrow: byTrade(t, 'Introducing / The gloss refresh', 'Introducing / Beard detailing', 'Introducing / The reset facial'), headline: byTrade(t, 'A glossier\nfinish.', 'A sharper\nfinish.', 'A brighter\nfinish.'), cta: 'See the service, book in bio' }),
      },
    },
  },
  priceList: {
    label: 'Price list', short: 'Prices', blurb: 'Your services and prices',
    photos: [],
    fields: [
      { key: 'title', label: 'Title', type: 'text', hint: ITALIC_HINT },
      { key: 'items', label: 'Services', type: 'priceList', max: 12, hint: 'Four fit on each picture. More make a carousel.' },
      { key: 'note', label: 'Note', type: 'text', placeholder: 'Prices from. Longer hair may cost more.' },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: (t) => ({
      title: 'Our menu.',
      items: byTrade(t,
        [{ name: 'Cut and blow-dry', price: '£48', detail: '45 min' }, { name: 'Gloss and tone', price: '£55', detail: '60 min' }, { name: 'Full head colour', price: '£95', detail: 'From 2 hrs' }, { name: 'Balayage', price: '£140', detail: 'From 3 hrs' }],
        [{ name: 'Skin fade', price: '£28', detail: '40 min' }, { name: 'Scissor cut', price: '£25', detail: '30 min' }, { name: 'Beard trim', price: '£15', detail: '20 min' }, { name: 'Hot towel shave', price: '£30', detail: '40 min' }],
        [{ name: 'BIAB manicure', price: '£35', detail: '60 min' }, { name: 'Lash lift and tint', price: '£45', detail: '50 min' }, { name: 'Brow shape', price: '£18', detail: '20 min' }, { name: 'Signature facial', price: '£65', detail: '60 min' }]),
      note: '', cta: CTA,
    }),
    designs: {
      editorial: { name: 'The menu', content: () => ({ title: 'Our *menu*.' }) },
      poster: { name: 'The line-up', content: () => ({ title: 'The line-up.', cta: 'Pick your cut, book in bio' }) },
      quiet: { name: 'The treatment list', content: (t) => ({ title: byTrade(t, 'Our *menu*.', 'Our *menu*.', '*Treatments*.') }) },
    },
  },
  tips: {
    label: 'Tip', short: 'Tip', blurb: 'One useful tip people save',
    photos: [],
    fields: [
      { key: 'label', label: 'Small heading', type: 'text' },
      { key: 'number', label: 'Number', type: 'text', placeholder: '01', only: [...STYLES, 'oneTip'] },
      { key: 'headline', label: 'Headline', type: 'text', hint: ITALIC_HINT },
      { key: 'body', label: 'The tip', type: 'textarea', only: [...STYLES, 'oneTip'] },
      { key: 'steps', label: 'Three steps', type: 'list', itemLabel: 'Step', max: 3, only: ['steps', 'checklist'], fullHint: 'Three fit this design.' },
      { key: 'detail', label: 'Small line', type: 'text', only: ['oneTip', 'steps', 'checklist'] },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: (t) => ({
      label: 'A useful little tip', number: '01',
      headline: byTrade(t, 'A good cut. Made yours.', 'Keep the fade fresh.', { nails: 'Make nails last.', lashes: 'Make lashes last.', skin: 'Glow between visits.', beauty: 'Make it last.' }),
      body: byTrade(t, 'Bring a reference photo to your appointment. Tell us what you love, and how much time you spend styling.', 'Book in every two to three weeks, before the lines soften. A matte clay gives natural hold.', { nails: 'Use cuticle oil every night and wear gloves for washing up. Your nails will thank you.', lashes: 'Keep lashes dry for the first day, then brush them through each morning with a clean spoolie.', skin: 'Double cleanse in the evening and wear SPF every day, even when it’s grey out.', beauty: 'Book your next visit before you leave, so the time you want is still there.' }),
      steps: byTrade(t, ['Bring a reference photo', 'Tell us about your routine', 'Ask how to recreate the finish'], ['Bring a photo of the cut', 'Tell us how you style it', 'Ask which products to use'], ['Bring a photo you love', 'Tell us about your routine', 'Ask how to make it last']),
      detail: '',
      cta: 'Save for your next appointment',
    }),
    designs: {
      editorial: { name: 'The useful little tip', content: (t) => ({ headline: byTrade(t, 'A good cut. *Made yours.*', 'Keep the fade *fresh*.', { nails: 'Make nails *last*.', lashes: 'Make lashes *last*.', skin: 'Glow *between visits*.', beauty: 'Make it *last*.' }) }) },
      poster: { name: 'Pro tip', content: () => ({ label: 'Pro tip' }) },
      quiet: { name: 'A small ritual', content: () => ({ label: 'A small ritual' }) },
    },
    layouts: {
      oneTip: {
        name: 'The one good tip', blurb: 'One tip, big and bold', theme: 'electric', ground: 'accent',
        content: (t) => ({
          label: 'One good tip',
          headline: byTrade(t, 'SHOW US\nTHE PHOTO.', 'KEEP THE\nFADE FRESH.', 'OIL YOUR\nCUTICLES.'),
          body: byTrade(t, 'A reference photo starts the conversation. Tell us which parts you love, so we can work out what fits your hair and routine.', 'Book in every two to three weeks, before the lines soften. A matte clay gives natural hold.', { nails: 'A drop of cuticle oil every night keeps nails flexible, so they bend instead of breaking.', lashes: 'No oil-based cleansers near the eyes: oil softens the bond and lashes drop sooner.', skin: 'SPF every morning, whatever the weather. It’s the one step that shows in five years.', beauty: 'A little care between visits keeps everything looking its best for longer.' }),
          detail: byTrade(t, 'Inspiration, not a one-size-fits-all result.', 'Sharp lines, every time.', { nails: 'Small habit. Stronger nails.', lashes: 'Small habit. Longer lift.', skin: 'Small habit. Brighter skin.', beauty: 'Small habit. Big difference.' }),
          cta: 'Save this, share your inspiration',
        }),
      },
      steps: {
        name: 'The three-step guide', blurb: 'Three short steps people save', theme: 'electric',
        content: () => ({ label: 'The quick guide', headline: 'Your next visit.\nMade easy.', detail: 'Three small things. A better chat.', cta: 'Save this for your next appointment' }),
      },
      checklist: {
        name: 'The appointment checklist', blurb: 'Three things to do before they come in', theme: 'sage',
        content: (t) => ({
          label: 'The appointment checklist', headline: 'Before you\ntake a seat.',
          steps: byTrade(t, ['Check your appointment time', 'Bring any inspiration photos', 'Tell us about recent hair changes'], ['Check your appointment time', 'Bring a photo of the cut you want', 'Tell us about any skin sensitivity'], ['Check your appointment time', 'Come with clean, product-free skin', 'Tell us about any allergies']),
          detail: 'Good preparation. A clearer conversation.', cta: 'Save for your next visit',
        }),
      },
    },
  },
  question: {
    label: 'Question', short: 'Question', blurb: 'Answer something people ask',
    photos: [],
    fields: [
      { key: 'eyebrow', label: 'Small heading', type: 'text', only: ['card', 'myth'] },
      { key: 'headline', label: 'The question', type: 'text', hint: ITALIC_HINT },
      { key: 'thought', label: 'What people think', type: 'text', only: ['myth'] },
      { key: 'body', label: 'Your answer', type: 'textarea' },
      { key: 'detail', label: 'Small line', type: 'text', only: ['card', 'myth'] },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: (t) => ({
      eyebrow: 'A question we get a lot',
      headline: byTrade(t, 'Not sure what to book?', 'Not sure what to ask for?', 'Not sure what to book?'),
      thought: '',
      body: byTrade(t, 'Tell us what you’re looking for. We’ll talk you through the options and help you choose.', 'Show us a photo or tell us the look. We’ll sort the rest.', 'Tell us what you’re looking for. We’ll talk you through the treatments and help you choose.'),
      detail: 'Ask before you book',
      cta: 'Message us to find your fit',
    }),
    designs: {
      editorial: { name: 'You asked', content: (t) => ({ headline: byTrade(t, 'Not sure what to *book*?', 'Not sure what to *ask for*?', 'Not sure what to *book*?') }) },
      poster: { name: 'Big question' },
      quiet: { name: 'A note from the studio' },
    },
    layouts: {
      card: { name: 'The question card', blurb: 'One question, one short answer', theme: 'paper', content: (t) => ({ headline: byTrade(t, 'Not sure\nwhat to book?', 'Not sure\nwhat to ask for?', 'Not sure\nwhat to book?'), cta: 'Message us, we’re happy to help' }) },
      myth: {
        name: 'This or that?', blurb: 'What people think, and what you’d tell them', theme: 'rose',
        content: (t) => ({
          eyebrow: 'Let’s talk about it',
          headline: byTrade(t, 'A big change?\nNot always.', 'A whole new cut?\nNot always.', 'A big change?\nNot always.'),
          thought: byTrade(t, 'I need a completely new look.', 'I need a completely new cut.', 'I need a whole new routine.'),
          body: 'A small adjustment can be the right starting point. Talk it through with us.',
          detail: 'Your look. Your comfort zone.',
          cta: 'Save this, bring your ideas',
        }),
      },
    },
  },
  offer: {
    label: 'Offer', short: 'Offer', blurb: 'A deal, with the full price shown',
    photos: [],
    fields: [
      { key: 'eyebrow', label: 'Small heading', type: 'text' },
      { key: 'value', label: 'The offer', type: 'text', placeholder: '£10 off', hint: ITALIC_HINT },
      { key: 'service', label: 'What it’s on', type: 'text' },
      { key: 'price', label: 'Full price', type: 'text', hint: 'Always show the full price. Hidden charges aren’t allowed.' },
      { key: 'dates', label: 'When', type: 'text' },
      { key: 'cta', label: 'Booking line', type: 'text' },
    ],
    content: (t) => ({
      eyebrow: 'This week only', value: '£10 off',
      service: byTrade(t, 'any cut and finish', 'any cut, Tuesday to Thursday', 'any gel manicure'),
      price: byTrade(t, 'Was £48, now £38', 'Was £28, now £18', 'Was £35, now £25'),
      dates: 'Tuesday to Thursday', cta: CTA,
    }),
    designs: {
      editorial: { name: 'The invitation' },
      poster: { name: 'The deal' },
      quiet: { name: 'A little treat', content: () => ({ value: '£10 *off*' }) },
    },
  },
  giftVouchers: {
    label: 'Gift vouchers', short: 'Vouchers', blurb: 'Sell gift vouchers',
    photos: [],
    fields: [
      { key: 'eyebrow', label: 'Small heading', type: 'text' },
      { key: 'headline', label: 'Headline', type: 'text', hint: ITALIC_HINT },
      { key: 'from', label: 'Price', type: 'text', placeholder: 'From £25' },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: () => ({ eyebrow: 'Gift vouchers', headline: 'The gift they’ll actually use.', from: 'From £25', cta: 'Buy online, link in bio' }),
    designs: {
      editorial: { name: 'The gift', content: () => ({ headline: 'The gift they’ll *actually use*.' }) },
      poster: { name: 'Gift it', content: () => ({ headline: 'Gift it.' }) },
      quiet: { name: 'Something lovely', content: () => ({ headline: 'Something *lovely*.' }) },
    },
  },
  // A season, a date or some news, in words alone. Campaigns open, remind and
  // close with it, in a season's own designs when it has them (../kits). Not
  // in TYPE_ORDER, so Social's gallery offers it only as those designs.
  announce: {
    label: 'Announcement', short: 'News', blurb: 'A season, a date or some news',
    photos: [],
    fields: [
      { key: 'eyebrow', label: 'Small heading', type: 'text' },
      { key: 'headline', label: 'Headline', type: 'text', hint: ITALIC_HINT },
      { key: 'body', label: 'A line about it', type: 'textarea' },
      { key: 'cta', label: 'Last line', type: 'text' },
    ],
    content: () => ({ eyebrow: 'News', headline: 'Something *new*.', body: '', cta: 'Book online, link in bio' }),
    designs: {
      editorial: { name: 'The announcement' },
      poster: { name: 'Say it loud' },
      quiet: { name: 'A gentle note' },
    },
  },
}

export const TYPE_ORDER = ['freeSlots', 'oneSlot', 'freshWork', 'beforeAfter', 'review', 'team', 'newService', 'priceList', 'tips', 'question', 'offer', 'giftVouchers']

// How each job is shown "as designed": its own family and colourway, chosen
// so the templates show the whole range side by side (your concepts' own
// pairings where there was one)
const SHOWCASE = {
  freeSlots: ['editorial', 'burgundy'],
  oneSlot: ['poster', 'cobalt'],
  freshWork: ['poster', 'pink'],
  beforeAfter: ['editorial', 'blush'],
  review: ['poster', 'red'],
  team: ['quiet', 'sage'],
  newService: ['layouts', 'electric', 'launch'],
  priceList: ['poster', 'cobalt'],
  tips: ['editorial', 'burgundy'],
  question: ['quiet', 'peach'],
  offer: ['poster', 'orange'],
  giftVouchers: ['quiet', 'butter'],
  announce: ['editorial', 'forest'],
}
export const showcaseFor = (type) => {
  const [family, colourway, layout] = SHOWCASE[type] || ['editorial', 'burgundy']
  return layout ? { family, colourway, layout } : { family, colourway }
}

// ─── Designs ───
// Which design a post is in: its style, or its layout
export const designKey = (post) => (post && post.family === 'layouts' ? post.layout : post && post.family)
export const hasStyles = (type) => !!(POST_TYPES[type] && Object.keys(POST_TYPES[type].designs || {}).length)
export const layoutsOf = (type) => Object.keys((POST_TYPES[type] && POST_TYPES[type].layouts) || {})
export const layoutOf = (type, layout) => ((POST_TYPES[type] && POST_TYPES[type].layouts) || {})[layout] || (type === 'announce' && seasonalLayout(layout)) || null
// Every design a job comes in: the three styles, then its layouts
export const designsFor = (type) => [
  ...(hasStyles(type) ? FAMILY_ORDER.map(family => ({ family })) : []),
  ...layoutsOf(type).map(layout => ({ family: 'layouts', layout })),
]
// The fields and photos the post's design uses
const mine = (post) => (x) => !x.only || x.only.includes(designKey(post))
// A layout can take more of a list than the styles do (four times, not three)
export const fieldsFor = (post) => POST_TYPES[post.type].fields.filter(mine(post))
  .map(f => (post.family === 'layouts' && f.layoutMax ? { ...f, max: f.layoutMax, fullHint: f.layoutHint || f.fullHint } : f))
export const photosFor = (post) => POST_TYPES[post.type].photos.filter(mine(post))

export const designName = (type, family, layout) => {
  const def = POST_TYPES[type]
  if (!def) return ''
  const d = family === 'layouts' ? layoutOf(type, layout) : def.designs[family]
  return (d && d.name) || ''
}
// A design's own first words, over the job's
// The trade as the words read it: a beauty business's specialism rides along
const wordsTrade = (trade, specialism) => (trade === 'beauty' && specialism ? `beauty:${specialism}` : trade)
const wordsFor = (type, family, trade, layout) => {
  const def = POST_TYPES[type]
  const d = family === 'layouts' ? layoutOf(type, layout) : def.designs[family]
  return { ...def.content(trade), ...((d && d.content && d.content(trade)) || {}) }
}

// The design a job can wear nearest the one asked for: the same one when the
// job has it, else the same colours in another of its designs (a job without
// layouts goes to its own showcase style; a job with only layouts, to its
// first layout)
export const designFor = (type, { family = 'editorial', colourway, layout } = {}) => {
  const layouts = layoutsOf(type)
  if (family === 'layouts') {
    if (layoutOf(type, layout)) return { family, layout, colourway }
    if (layouts.length) return { family, layout: layouts[0], colourway }
    const show = showcaseFor(type)
    return { family: show.family, layout: null, colourway: colourway && colourwayInFamily(colourway, 'layouts', show.family) }
  }
  if (hasStyles(type) || !layouts.length) return { family: FAMILY_ORDER.includes(family) ? family : 'editorial', layout: null, colourway }
  return { family: 'layouts', layout: layouts[0], colourway: colourway && colourwayInFamily(colourway, family, 'layouts') }
}

// The colourway a new post starts in: the salon's brand when it has a brand
// colour, else a layout's own theme, else the family's first
export const firstColourway = (family, brandColour, type, layout) => {
  if (brandColour) return 'brand'
  const l = family === 'layouts' && layoutOf(type, layout)
  if (l && l.theme) return l.theme
  return (COLOURWAYS[family] || COLOURWAYS.editorial)[0].id
}

// A new post, ready to edit
export const makePost = (type, { trade = 'hair', specialism = null, family = 'editorial', colourway, layout, brandColour, content = {}, photos = {}, source, format } = {}) => {
  const def = POST_TYPES[type]
  const d = designFor(type, { family, colourway, layout })
  return {
    type,
    family: d.family,
    ...(d.layout ? { layout: d.layout } : {}),
    colourway: d.colourway || firstColourway(d.family, brandColour, type, d.layout),
    format: format || 'post',
    status: 'draft',
    trade,
    source: source || null,
    ...(specialism ? { specialism } : {}),
    content: { ...wordsFor(type, d.family, wordsTrade(trade, specialism), d.layout), ...content },
    // Words given here (an idea's real review, a real free slot) count as
    // the salon's own, so a new design never replaces them
    touched: Object.keys(content || {}).reduce((a, k) => ({ ...a, [k]: true }), {}),
    photos: { ...((def.stock && def.stock(trade)) || {}), ...photos },
    caption: null,
  }
}

// The facts in each kind's example words that only the salon can know: a
// price, a time, a name, a voucher amount. A design shows them as examples
// ("Was £48, now £38", "From £25", "Colour specialist"); a post made for the
// salon must never send one out as theirs.
export const EXAMPLE_FACTS = {
  offer: ['eyebrow', 'value', 'service', 'price', 'dates'],
  giftVouchers: ['from'],
  oneSlot: ['time', 'service'],
  freeSlots: ['times'],
  freshWork: ['detail'],
  team: ['name', 'role', 'line'],
  beforeAfter: ['detail'],
  review: ['quote', 'name', 'service'],
  priceList: ['items'],
  newService: ['headline', 'body', 'items', 'detail'],
}
const isGiven = v => !(v === '' || v == null || (Array.isArray(v) && !v.length))

// A post made only from what's given: the design's example facts emptied
// unless the content gives the real ones, and no stock photo unless one is
// passed in on purpose.
export const realPost = (type, opts = {}) => {
  const content = opts.content || {}
  const p = makePost(type, { ...opts, content })
  const next = { ...p.content }
  ;(EXAMPLE_FACTS[type] || []).forEach((k) => {
    if (!isGiven(content[k])) next[k] = Array.isArray(next[k]) ? [] : ''
  })
  return { ...p, content: next, photos: { ...(opts.photos || {}) } }
}

// The same post in another design: its colours move across, and any words
// the salon hasn't changed become that design's own
export const restyle = (post, family, colourway, layout) => {
  const d = designFor(post.type, { family, colourway, layout })
  const before = wordsFor(post.type, post.family, wordsTrade(post.trade, post.specialism), post.layout)
  const after = wordsFor(post.type, d.family, wordsTrade(post.trade, post.specialism), d.layout)
  const touched = post.touched || {}
  const content = { ...post.content }
  Object.keys(after).forEach((k) => {
    if (touched[k]) return
    // Keep words that were never the old design's defaults (older posts).
    // Lists (times, steps, three things) are compared by what they say
    if (content[k] !== undefined && before[k] !== undefined && JSON.stringify(content[k]) !== JSON.stringify(before[k])) return
    content[k] = after[k]
  })
  // layout stays as a key (undefined for a style), so merging it into the
  // post clears an old one
  return { ...post, family: d.family, layout: d.layout || undefined, colourway: d.colourway || colourwayInFamily(post.colourway, post.family, d.family), content, caption: post.caption && post.caption.edited ? post.caption : null }
}

// Changing job keeps the design and colours (or the nearest the job has)
export const changeType = (post, type) => {
  const next = makePost(type, { trade: post.trade, family: post.family, colourway: post.colourway, layout: post.layout })
  return { ...post, type, family: next.family, layout: next.layout, colourway: next.colourway, content: next.content, photos: next.photos, touched: {}, caption: null, source: null }
}

// Price lists run to more than one picture; everything else is one
export const slidesFor = (post) => {
  if (post.type === 'priceList') {
    const n = Math.max(1, Math.ceil(((post.content && post.content.items) || []).filter(it => it && it.name).length / 4))
    return Array.from({ length: n }, (_, i) => ({ kind: 'list', page: i }))
  }
  return [{ kind: 'single' }]
}

// Posts made before the design families (layout + look) wear their family
// from the brand and keep their words
export const upgradePost = (post, { family = 'editorial', brandColour } = {}) => {
  if (!post || post.family) return post
  const c = { ...(post.content || {}) }
  const plain = (s) => String(s || '').replace(/\*/g, '')
  if (post.type === 'tips' && Array.isArray(c.tips)) {
    const tips = c.tips.filter(Boolean)
    Object.assign(c, { label: 'A useful little tip', number: '01', headline: plain(c.title), body: tips.join(' ') })
  }
  if (post.type === 'freshWork' && !c.detail) c.detail = c.sub || c.eyebrow || ''
  if (post.type === 'beforeAfter' && !c.detail) c.detail = c.sub || ''
  if (post.type === 'priceList' && !c.cta) c.cta = CTA
  const known = POST_TYPES[post.type] ? post.type : 'freshWork'
  const { layout, look, ...rest } = post
  return {
    ...rest,
    type: known,
    family,
    colourway: firstColourway(family, brandColour),
    content: { ...wordsFor(known, family, post.trade || 'hair'), ...c },
    touched: Object.keys(c).reduce((a, k) => ({ ...a, [k]: true }), {}),
    photos: known === 'beforeAfter' ? {} : (post.photos || {}),
  }
}

export { FAMILY_ORDER }
